var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { FormattedMessage, FormattedDate, useIntl } from "react-intl";
import { Accordion, Badge, Button, KeyValue, Modal, MultiColumnList, Row, Col, Loading, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery } from "react-query";
import { fetchAuditLogs, fetchAuditLogDetail } from "../api/auditLogService";
/** Earliest possible date for audit log queries (lower bound for dateFrom filter). */
var AUDIT_LOG_EPOCH = "2020-01-01T00:00:00Z";
var AuditLogAccordion = function (_a) {
    var entityId = _a.entityId, entityType = _a.entityType, _b = _a.labelId, labelId = _b === void 0 ? "ui-czna-users.users.section.history" : _b, _c = _a.defaultOpen, defaultOpen = _c === void 0 ? true : _c;
    var intl = useIntl();
    var ky = useOkapiKy();
    var _d = React.useState(defaultOpen), isOpen = _d[0], setIsOpen = _d[1];
    var _f = React.useState(1), page = _f[0], setPage = _f[1];
    var _g = React.useState(null), selectedEntryId = _g[0], setSelectedEntryId = _g[1];
    var _h = useQuery(["czna-users", "audit-logs", entityType || "related", entityId, page], function () {
        return fetchAuditLogs(ky, __assign(__assign({}, (entityType
            ? { entityType: entityType, relatedTo: entityId }
            : { relatedTo: entityId })), { page: page, dateFrom: AUDIT_LOG_EPOCH }));
    }, {
        enabled: isOpen,
        keepPreviousData: true,
    }), logsData = _h.data, isLoading = _h.isLoading;
    var _j = useQuery(["czna-users", "audit-log-detail", selectedEntryId], function () { return fetchAuditLogDetail(ky, selectedEntryId); }, {
        enabled: !!selectedEntryId,
    }), detailData = _j.data, isDetailLoading = _j.isLoading;
    var handleToggle = function () {
        setIsOpen(function (prev) { return !prev; });
    };
    var handleRowClick = function (_e, row) {
        setSelectedEntryId(row.id);
    };
    var handleCloseModal = function () {
        setSelectedEntryId(null);
    };
    var formatter = {
        createdAt: function (row) { return (React.createElement(FormattedDate, { value: row.createdAt, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })); },
        actionType: function (row) {
            var id = "ui-czna-users.auditLog.actionType.".concat(row.actionType);
            return intl.formatMessage({ id: id, defaultMessage: row.actionType });
        },
        summary: function (row) { return row.summary; },
        status: function (row) {
            var id = "ui-czna-users.auditLog.status.".concat(row.status);
            return intl.formatMessage({ id: id, defaultMessage: row.status });
        },
    };
    var columnMapping = {
        createdAt: intl.formatMessage({
            id: "ui-czna-users.auditLog.column.createdAt",
        }),
        actionType: intl.formatMessage({
            id: "ui-czna-users.auditLog.column.actionType",
        }),
        summary: intl.formatMessage({
            id: "ui-czna-users.auditLog.column.summary",
        }),
        status: intl.formatMessage({ id: "ui-czna-users.auditLog.column.status" }),
    };
    var renderChangesTable = function () {
        if (!(detailData === null || detailData === void 0 ? void 0 : detailData.changes))
            return null;
        var fields = detailData.changes.fields || [];
        if (fields.length === 0)
            return null;
        return (React.createElement("table", null,
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null,
                        React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.field" })),
                    React.createElement("th", null,
                        React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.oldValue" })),
                    React.createElement("th", null,
                        React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.newValue" })))),
            React.createElement("tbody", null, fields.map(function (field) {
                var _a, _b;
                return (React.createElement("tr", { key: field },
                    React.createElement("td", null, field),
                    React.createElement("td", null, ((_a = detailData.oldValue) === null || _a === void 0 ? void 0 : _a[field]) != null
                        ? String(detailData.oldValue[field])
                        : "-"),
                    React.createElement("td", null, ((_b = detailData.newValue) === null || _b === void 0 ? void 0 : _b[field]) != null
                        ? String(detailData.newValue[field])
                        : "-")));
            }))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: labelId }), open: isOpen, onToggle: handleToggle, displayWhenClosed: logsData ? React.createElement(Badge, null, logsData.total) : undefined }, isLoading && !logsData ? (React.createElement(Loading, null)) : logsData && logsData.data.length > 0 ? (React.createElement(React.Fragment, null,
            React.createElement(MultiColumnList, { contentData: logsData.data, visibleColumns: ["createdAt", "actionType", "summary", "status"], columnMapping: columnMapping, formatter: formatter, onRowClick: handleRowClick, interactive: true }),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12, style: {
                        display: "flex",
                        justifyContent: "center",
                        gap: "1rem",
                        padding: "0.5rem 0",
                    } },
                    React.createElement(Button, { disabled: page <= 1, onClick: function () { return setPage(function (p) { return p - 1; }); } },
                        React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.previousPage" })),
                    React.createElement("span", null,
                        React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.pageInfo", values: { page: page, totalPages: logsData.totalPages } })),
                    React.createElement(Button, { disabled: page >= logsData.totalPages, onClick: function () { return setPage(function (p) { return p + 1; }); } },
                        React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.nextPage" })))))) : (React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.empty" }))),
        selectedEntryId && (React.createElement(Modal, { open: true, label: intl.formatMessage({
                id: "ui-czna-users.auditLog.detail.title",
            }), onClose: handleCloseModal, dismissible: true }, isDetailLoading ? (React.createElement(Loading, null)) : detailData ? (React.createElement(React.Fragment, null,
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.createdAt" }), value: React.createElement(FormattedDate, { value: detailData.createdAt, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) })),
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.userName" }), value: detailData.userName }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.actionType" }), value: intl.formatMessage({
                            id: "ui-czna-users.auditLog.actionType.".concat(detailData.actionType),
                            defaultMessage: detailData.actionType,
                        }) })),
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.status" }), value: intl.formatMessage({
                            id: "ui-czna-users.auditLog.status.".concat(detailData.status),
                            defaultMessage: detailData.status,
                        }) }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.ipAddress" }), value: detailData.ipAddress })),
                React.createElement(Col, { xs: 6 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.userAgent" }), value: detailData.userAgent || "-" }))),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.column.summary" }), value: detailData.summary }))),
            detailData.errorMessage && (React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.auditLog.detail.errorMessage" }), value: detailData.errorMessage })))),
            renderChangesTable())) : null))));
};
export default AuditLogAccordion;
